import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { UploadIcon, LockIcon, LogOutIcon, ImagePlusIcon, Loader2Icon, CheckCircleIcon, Trash2Icon } from 'lucide-react';
type CustomPhoto = {
  src: string;
  alt: string;
  year: string;
};
export function SettingsPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('fch_admin_logged_in') === 'true');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  // Cloudinary settings
  const [cloudName, setCloudName] = useState(localStorage.getItem('fch_cloud_name') || '');
  const [uploadPreset, setUploadPreset] = useState(localStorage.getItem('fch_upload_preset') || '');
  const [selectedYear, setSelectedYear] = useState('2026');
  // Custom photos state
  const [customPhotos, setCustomPhotos] = useState<CustomPhoto[]>([]);
  const loadPhotos = () => {
    const saved = localStorage.getItem('fch_custom_photos');
    if (saved) {
      try {
        setCustomPhotos(JSON.parse(saved));
      } catch (e) {
        console.error('Chyba při načítání fotek', e);
      }
    } else {
      setCustomPhotos([]);
    }
  };
  // Load photos on mount and when login state changes
  useEffect(() => {
    if (isLoggedIn) {
      loadPhotos();
    }
  }, [isLoggedIn]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === 'kasspi' && password === '2368') {
      setIsLoggedIn(true);
      localStorage.setItem('fch_admin_logged_in', 'true');
      setError('');
    } else {
      setError('Nesprávné přihlašovací údaje.');
    }
  };
  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('fch_admin_logged_in');
    localStorage.removeItem('fch_global_authorized');
    setUsername('');
    setPassword('');
    window.location.href = '/';
  };
  const handleSaveSettings = () => {
    localStorage.setItem('fch_cloud_name', cloudName);
    localStorage.setItem('fch_upload_preset', uploadPreset);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 3000);
  };
  const uploadToCloudinary = async (file: File) => {
    if (!cloudName || !uploadPreset) {
      alert('Prosím vyplňte Cloud Name a Upload Preset.');
      return;
    }
    setIsUploading(true);
    setUploadSuccess(false);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset);
    try {
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.secure_url) {
        // Save to localStorage for the Gallery page to pick up
        const existingPhotos = JSON.parse(localStorage.getItem('fch_custom_photos') || '[]');
        const newPhoto = {
          src: data.secure_url,
          alt: 'Nahraná fotografie',
          year: selectedYear
        };
        const updatedPhotos = [...existingPhotos, newPhoto];
        localStorage.setItem('fch_custom_photos', JSON.stringify(updatedPhotos));
        setCustomPhotos(updatedPhotos);
        // Save settings
        localStorage.setItem('fch_cloud_name', cloudName);
        localStorage.setItem('fch_upload_preset', uploadPreset);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      } else {
        throw new Error(data.error?.message || 'Chyba při nahrávání');
      }
    } catch (err) {
      alert('Nahrávání selhalo: ' + (err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      uploadToCloudinary(file);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadToCloudinary(file);
    }
  };
  const handleDeletePhoto = (index: number) => {
    if (!window.confirm('Opravdu chcete tuto fotografii smazat z galerie?')) return;
    const updatedPhotos = customPhotos.filter((_, i) => i !== index);
    localStorage.setItem('fch_custom_photos', JSON.stringify(updatedPhotos));
    setCustomPhotos(updatedPhotos);
  };
  if (!isLoggedIn) {
    return <main className="pt-32 pb-24 min-h-screen bg-off-white flex items-center justify-center px-6" data-id="element-294">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full" data-id="element-295">
          <div className="flex justify-center mb-6" data-id="element-296">
            <div className="bg-mist p-4 rounded-full text-blue-medium" data-id="element-297">
              <LockIcon className="w-8 h-8" data-id="element-298" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-navy-deep text-center mb-8" data-id="element-299">
            Přihlášení do administrace
          </h1>

          <form onSubmit={handleLogin} className="space-y-6" data-id="element-300">
            <div data-id="element-301">
              <label className="block text-sm font-medium text-navy-deep/80 mb-2" data-id="element-302">
                Uživatelské jméno
              </label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-mist focus:border-blue-medium focus:ring-2 focus:ring-blue-medium/20 outline-none transition-all" placeholder="Zadejte jméno" data-id="element-303" />
            </div>
            <div data-id="element-304">
              <label className="block text-sm font-medium text-navy-deep/80 mb-2" data-id="element-305">
                Heslo
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-mist focus:border-blue-medium focus:ring-2 focus:ring-blue-medium/20 outline-none transition-all" placeholder="Zadejte heslo" data-id="element-306" />
            </div>

            {error && <p className="text-red-500 text-sm text-center" data-id="element-307">{error}</p>}

            <button type="submit" className="w-full bg-blue-medium text-white py-3 rounded-lg font-medium hover:bg-blue-sky transition-colors" data-id="element-308">
              Přihlásit se
            </button>
          </form>
        </motion.div>
      </main>;
  }
  return <main className="pt-32 pb-24 min-h-screen bg-off-white" data-id="element-309">
      <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-310">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-311">
          <div className="flex justify-between items-center mb-12 border-b border-mist pb-6" data-id="element-312">
            <h1 className="text-3xl font-light text-navy-deep" data-id="element-313">
              Správa galerie
            </h1>
            <button onClick={handleLogout} className="flex items-center gap-2 text-navy-deep/60 hover:text-red-500 transition-colors" data-id="element-314">
              <LogOutIcon className="w-5 h-5" data-id="element-315" />
              <span data-id="element-316">Odhlásit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-317">
            {/* Settings Sidebar */}
            <div className="bg-white rounded-2xl shadow-sm border border-mist p-6 h-fit" data-id="element-318">
              <h2 className="text-lg font-semibold text-navy-deep mb-4" data-id="element-319">
                Nastavení Cloudinary
              </h2>
              <div className="space-y-4" data-id="element-320">
                <div data-id="element-321">
                  <label className="block text-xs font-bold text-navy-deep/50 uppercase mb-1" data-id="element-322">
                    Cloud Name
                  </label>
                  <input type="text" value={cloudName} onChange={e => setCloudName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-mist text-sm outline-none focus:border-blue-medium" placeholder="Např. dxy12345" data-id="element-323" />
                </div>
                <div data-id="element-324">
                  <label className="block text-xs font-bold text-navy-deep/50 uppercase mb-1" data-id="element-325">
                    Upload Preset (Unsigned)
                  </label>
                  <input type="text" value={uploadPreset} onChange={e => setUploadPreset(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-mist text-sm outline-none focus:border-blue-medium" placeholder="Např. ml_default" data-id="element-326" />
                </div>
              </div>
              <button onClick={handleSaveSettings} className="w-full mt-4 bg-navy-deep text-white py-2 rounded-lg text-sm font-medium hover:bg-navy-deep/90 transition-all flex items-center justify-center gap-2" data-id="element-327">
                {settingsSaved ? <>
                    <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-328" />
                    Uloženo
                  </> : 'Uložit nastavení'}
              </button>
            </div>

            {/* Upload Area */}
            <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-mist p-8" data-id="element-329">
              <h2 className="text-xl font-semibold text-navy-deep mb-6 flex items-center gap-3" data-id="element-330">
                <ImagePlusIcon className="w-6 h-6 text-blue-medium" data-id="element-331" />
                Nahrát nové fotografie
              </h2>

              <div className="mb-6" data-id="element-332">
                <label className="block text-sm font-medium text-navy-deep/80 mb-2" data-id="element-333">
                  Vyberte ročník
                </label>
                <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="w-full max-w-xs px-4 py-3 rounded-lg border border-mist focus:border-blue-medium focus:ring-2 focus:ring-blue-medium/20 outline-none bg-white" data-id="element-334">
                  <option value="2026" data-id="element-335">Ročník 2026</option>
                  <option value="2025" data-id="element-336">Ročník 2025</option>
                </select>
              </div>

              <div onDragOver={e => {
              e.preventDefault();
              setIsDragging(true);
            }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-colors duration-200 ${isDragging ? 'border-blue-medium bg-blue-medium/5' : 'border-mist hover:border-blue-medium/50 hover:bg-off-white'} ${isUploading ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-337">
                <input type="file" onChange={handleFileSelect} className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" data-id="element-338" />
                <div className="flex justify-center mb-4" data-id="element-339">
                  <div className="bg-mist p-4 rounded-full text-blue-medium" data-id="element-340">
                    {isUploading ? <Loader2Icon className="w-8 h-8 animate-spin" data-id="element-341" /> : <UploadIcon className="w-8 h-8" data-id="element-342" />}
                  </div>
                </div>
                <h3 className="text-lg font-medium text-navy-deep mb-2" data-id="element-343">
                  {isUploading ? 'Nahrávám...' : 'Přetáhněte fotografie sem'}
                </h3>
                <p className="text-navy-deep/60 mb-6" data-id="element-344">
                  nebo klikněte pro výběr souborů z počítače
                </p>

                {uploadSuccess && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center justify-center gap-2 text-green-600 font-medium" data-id="element-345">
                    <CheckCircleIcon className="w-5 h-5" data-id="element-346" />
                    Hotovo! Fotka je v galerii.
                  </motion.div>}
              </div>
            </div>
          </div>

          {/* Uploaded photos management */}
          <div className="mt-8 bg-white rounded-2xl shadow-sm border border-mist p-8" data-id="element-347">
            <div className="flex justify-between items-center mb-6" data-id="element-348">
              <h2 className="text-xl font-semibold text-navy-deep" data-id="element-349">
                Nahrané fotografie ({customPhotos.length})
              </h2>
              <button onClick={loadPhotos} className="text-sm text-blue-medium hover:underline flex items-center gap-1" data-id="element-350">
                <Loader2Icon className="w-4 h-4" data-id="element-351" />
                Obnovit seznam
              </button>
            </div>
            {customPhotos.length === 0 ? <p className="text-navy-deep/50 text-sm" data-id="element-352">
                Zatím nebyly nahrány žádné fotografie.
              </p> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-353">
                {customPhotos.map((photo, i) => <div key={`${photo.src}-${i}`} className="group relative aspect-square overflow-hidden rounded-xl border border-mist bg-mist" data-id="element-354">
                    <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" data-id="element-355" />
                    <span className="absolute top-2 left-2 bg-navy-deep/80 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-356">
                      {photo.year}
                    </span>
                    <button type="button" onClick={() => handleDeletePhoto(i)} className="absolute top-2 right-2 bg-white/90 text-red-500 p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white transition-all" aria-label="Smazat fotografii" data-id="element-357">
                      <Trash2Icon className="w-4 h-4" data-id="element-358" />
                    </button>
                  </div>)}
              </div>}
          </div>
        </motion.div>
      </div>
    </main>;
}